import { useState, useRef } from "react";
import Modal from "../components/Modal";
import GuideBanner from "../components/GuideBanner";
import Field, { inputStyle, selectStyle, inputErrorStyle } from "../components/Field";
import { PARTNER_TYPES, BRAZILIAN_STATES, tipoParceiroPorAtividade } from "../constants";
import { generateId, getToday, validatePhone, validateEmail, maskPhone, maskCNPJ, maskCEP, fetchCNPJ, fetchCEP } from "../utils";

const empty = {
  name: "", type: "oficina", contact: "", phone: "", email: "",
  cnpj: "", region: "SP", cep: "", cidade: "", bairro: "", address: "", services: "", notes: "",
  razaoSocial: "", nomeFantasia: "",
};

export default function NewPartner({ open, onClose, setPartners, setTasks, userId, role = "comercial" }) {
  const [data, setData] = useState({ ...empty });
  const [errors, setErrors] = useState({});
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const tipoTocado = useRef(false); // escolha manual do ramo vence a sugestão do CNPJ
  const [cepLoading, setCepLoading] = useState(false);

  // Endereço digitado à mão numa tela de rua chega errado — e é por isso que
  // metade da base está sem cidade. O CEP resolve com oito dígitos.
  const handleCEP = async (val) => {
    const masked = maskCEP(val);
    setData((p) => ({ ...p, cep: masked }));
    if (masked.replace(/\D/g, "").length !== 8) return;
    setCepLoading(true);
    const r = await fetchCEP(masked);
    setCepLoading(false);
    if (!r) return;
    setData((p) => ({
      ...p,
      // O número não vem do CEP: mantém o que já foi digitado e só completa o
      // resto, para não apagar "nº 320" que a pessoa acabou de escrever.
      address: [r.rua, r.bairro].filter(Boolean).join(", ") || p.address,
      bairro: r.bairro || p.bairro,
      cidade: r.cidade || p.cidade,
      region: r.uf || p.region,
    }));
  };

  const validate = () => {
    const err = {};
    if (!data.name.trim()) err.name = "Nome do estabelecimento é obrigatório";
    if (data.phone && !validatePhone(data.phone)) err.phone = "Telefone inválido";
    if (data.email && !validateEmail(data.email)) err.email = "E-mail inválido";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleCNPJ = async (val) => {
    const masked = maskCNPJ(val);
    setData((p) => ({ ...p, cnpj: masked }));
    const digits = masked.replace(/\D/g, "");
    if (digits.length === 14) {
      setCnpjLoading(true);
      const result = await fetchCNPJ(digits);
      setCnpjLoading(false);
      if (result) {
        // A atividade do CNPJ já diz o ramo. Sem isto o campo ficava no primeiro
        // item da lista ("Oficina Mecânica") e ninguém reparava — foi assim que
        // postos e borracharias viraram oficina na Rede.
        const sugerido = tipoTocado.current ? null : tipoParceiroPorAtividade(result.atividadePrincipal);
        setData((p) => ({
          ...p,
          ...(sugerido ? { type: sugerido } : {}),
          name: p.name || result.nomeFantasia || result.razaoSocial,
          razaoSocial: result.razaoSocial,
          nomeFantasia: result.nomeFantasia,
          address: result.enderecoCompleto || p.address,
          // Cidade, UF, bairro e CEP vêm da Receita e ficam em campo próprio:
          // é por eles que o relatório de contatos por região desce até a
          // cidade — e quem cadastra pelo CNPJ não precisa digitar o CEP.
          cidade: result.cidade || p.cidade,
          region: result.uf || p.region,
          bairro: result.bairro || p.bairro,
          cep: result.cep ? maskCEP(result.cep) : p.cep,
        }));
      }
    }
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const today = getToday();
    const p = {
      // Admin/gerente cadastrando = empresa para o time (sem responsável), a
      // mesma regra do registro rápido. Comercial fica com o que cadastrou.
      ...data, id: generateId(), stage: "identificado", starred: false,
      userId: role === "admin" || role === "gerente" ? null : userId,
      createdAt: today, lastContact: null, docs: [], interactions: [],
      stageHistory: [{ from: null, to: "identificado", date: today, note: "Parceiro cadastrado" }],
    };
    setPartners((prev) => [...prev, p]);
    setTasks((prev) => [...prev, {
      id: generateId(), title: `1º contato — ${p.name}`, userId,
      type: "partner", refId: p.id, dueDate: today, priority: "medium", done: false,
      notes: "Novo parceiro cadastrado. Fazer abordagem inicial.",
    }]);
    setData({ ...empty });
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setData({ ...empty });
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Cadastrar Novo Parceiro da Rede">
      <GuideBanner title="Dica: Como cadastrar um parceiro" text="Cadastre o estabelecimento assim que identificá-lo. O mais importante é: nome e tipo (oficina, posto, etc.). Contato e telefone podem ser preenchidos depois ao registrar a primeira abordagem." />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="CNPJ" help="Se souber, preenche tudo pela Receita">
          <div style={{ position: "relative" }}>
            <input style={inputStyle} value={data.cnpj} onChange={(e) => handleCNPJ(e.target.value)} placeholder="00.000.000/0000-00" />
            {cnpjLoading && <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#64748b" }}>Buscando...</span>}
          </div>
        </Field>
        <Field label="Tipo" required>
          <select style={selectStyle} value={data.type} onChange={(e) => { tipoTocado.current = true; setData((p) => ({ ...p, type: e.target.value })); }}>
            {PARTNER_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Nome do Estabelecimento" required error={errors.name}>
            <input style={errors.name ? inputErrorStyle : inputStyle} value={data.name} onChange={(e) => setData((p) => ({ ...p, name: e.target.value }))} placeholder="Ex: Auto Center Exemplo" />
          </Field>
        </div>
        {data.razaoSocial && (
          <div style={{ gridColumn: "1/-1", padding: "6px 10px", borderRadius: 6, background: "#f0fdfa", border: "1px solid #99f6e4", marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: "#0f766e" }}><strong>Razão Social:</strong> {data.razaoSocial}</div>
            {data.nomeFantasia && <div style={{ fontSize: 10, color: "#0f766e" }}><strong>Nome Fantasia:</strong> {data.nomeFantasia}</div>}
          </div>
        )}
        <Field label="Contato (Responsável)" error={errors.contact}>
          <input style={errors.contact ? inputErrorStyle : inputStyle} value={data.contact} onChange={(e) => setData((p) => ({ ...p, contact: e.target.value }))} placeholder="Ex: José Carlos (opcional)" />
        </Field>
        <Field label="Telefone" error={errors.phone}>
          <input style={errors.phone ? inputErrorStyle : inputStyle} value={data.phone} onChange={(e) => setData((p) => ({ ...p, phone: maskPhone(e.target.value) }))} placeholder="(11) 99999-0000 (opcional)" />
        </Field>
        <Field label="E-mail" error={errors.email}>
          <input style={errors.email ? inputErrorStyle : inputStyle} value={data.email} onChange={(e) => setData((p) => ({ ...p, email: e.target.value }))} />
        </Field>
        <Field label="Estado (UF)">
          <select style={selectStyle} value={data.region} onChange={(e) => setData((p) => ({ ...p, region: e.target.value }))}>
            {BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf} — {s.name}</option>)}
          </select>
        </Field>
        <Field label="CEP" help={cepLoading ? "Buscando endereço..." : "Não sabe o CNPJ? O CEP preenche o endereço"}>
          <input style={inputStyle} value={data.cep} onChange={(e) => handleCEP(e.target.value)} placeholder="00000-000" inputMode="numeric" />
        </Field>
        <Field label="Cidade" help="Usada no relatório de contatos por região">
          <input style={inputStyle} value={data.cidade} onChange={(e) => setData((p) => ({ ...p, cidade: e.target.value }))} placeholder="Ex: Campinas" />
        </Field>
        <Field label="Bairro">
          <input style={inputStyle} value={data.bairro} onChange={(e) => setData((p) => ({ ...p, bairro: e.target.value }))} />
        </Field>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Endereço Completo">
            <input style={inputStyle} value={data.address} onChange={(e) => setData((p) => ({ ...p, address: e.target.value }))} placeholder="Rua, número — o CEP preenche o resto" />
          </Field>
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Serviços Oferecidos">
            <textarea style={{ ...inputStyle, minHeight: 40, resize: "vertical" }} value={data.services} onChange={(e) => setData((p) => ({ ...p, services: e.target.value }))} placeholder="Ex: Troca de óleo, alinhamento, balanceamento, freios" />
          </Field>
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Observações">
            <textarea style={{ ...inputStyle, minHeight: 40, resize: "vertical" }} value={data.notes} onChange={(e) => setData((p) => ({ ...p, notes: e.target.value }))} placeholder="Ex: Indicação do Marcos (Posto Bandeirante)." />
          </Field>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 6 }}>
        <button onClick={handleClose} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
        <button onClick={handleSubmit} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cadastrar Parceiro</button>
      </div>
    </Modal>
  );
}
